//src/pages/ClientCompanySelectionPage.tsx
import { Building2, CheckCircle2, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useClientCompanyContext } from "@/hooks/useClientCompanyContext";

export function ClientCompanySelectionPage() {
  const navigate = useNavigate();
  const {
    activeClientCompanies,
    isLoadingClientCompanies,
    refreshClientCompanies,
    selectClientCompany,
  } = useClientCompanyContext();

  if (isLoadingClientCompanies) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-5xl flex-col justify-center gap-4">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-4 w-[28rem]" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-36 rounded-2xl" />
          <Skeleton className="h-36 rounded-2xl" />
          <Skeleton className="h-36 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (activeClientCompanies.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="w-full max-w-xl border-dashed text-center">
          <CardHeader>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <Users className="h-7 w-7 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">Nenhum cliente disponível</CardTitle>
            <CardDescription>
              Seu usuário não possui acesso a nenhum cliente ativo. Solicite acesso ao administrador da Empresa.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="rounded-3xl border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Selecione um cliente para continuar
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Você possui acesso a mais de um cliente. Escolha abaixo qual cliente deseja gerenciar para carregar os menus e dados operacionais.
            </p>
          </div>

          <Button variant="outline" onClick={() => void refreshClientCompanies()}>
            Atualizar lista
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {activeClientCompanies.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => {
              selectClientCompany(client);
              navigate("/");
            }}
            className="group rounded-3xl border bg-card p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Building2 className="h-6 w-6" />
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-semibold text-foreground">{client.name}</h2>
                <p className="mt-1 truncate text-sm text-muted-foreground">
                  {client.legal_name || client.slug}
                </p>
                {client.document_number ? (
                  <p className="mt-2 text-xs text-muted-foreground">Documento: {client.document_number}</p>
                ) : null}
              </div>

              <CheckCircle2 className="h-5 w-5 shrink-0 text-muted-foreground transition group-hover:text-primary" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default ClientCompanySelectionPage;
